/** The three markets supported from day one. */
export type Locale = 'en' | 'es' | 'pt';

export const LOCALES = ['en', 'es', 'pt'] as const;
export const DEFAULT_LOCALE: Locale = 'en';
// Holds only a language the user picked; absent means "follow the browser".
// Keep in sync with index.html.
export const LOCALE_STORAGE_KEY = 'numtable.localeChoice';

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'es' || value === 'pt';
}

/** The base subtag of a BCP-47 tag: `pt-BR` -> `pt`. */
function baseSubtag(tag: string): string {
  const first = tag.toLowerCase().split('-')[0];
  return first ?? '';
}

function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage unavailable — the preference just won't survive a reload.
  }
}

/**
 * Resolution order (docs/ARCHITECTURE.md §9):
 *   profiles.locale -> localStorage -> navigator.languages -> 'en'
 *
 * `profileLocale` is passed in by the caller once a session exists; there is
 * no auth yet, so today it is always undefined.
 */
export function resolveLocale(profileLocale?: string | null): Locale {
  if (isLocale(profileLocale)) return profileLocale;

  const stored = readStoredLocale();
  if (stored) return stored;

  const preferred = navigator.languages ?? [navigator.language];
  for (const tag of preferred) {
    const base = baseSubtag(tag);
    if (isLocale(base)) return base;
  }

  return DEFAULT_LOCALE;
}

/**
 * The tag handed to `Intl`. We match messages on the base subtag but format
 * numbers and dates with the FULL navigator tag, so an Argentine sees
 * `1.234,56` and an American `1,234.56` while both read the same message file.
 */
export function resolveFormatLocale(
  locale: Locale,
  preferred: readonly string[] = navigator.languages ?? [navigator.language],
): string {
  const match = preferred.find((tag) => baseSubtag(tag) === locale);
  return match ?? locale;
}
